// Населённый пункт покупателя — общий для всего сайта.
//
// Раньше город жил внутри useDelivery и существовал только на странице
// корзины. Теперь он выбирается в шапке и нужен ещё и кнопке корзины,
// поэтому вынесен отдельно, а useDelivery берёт его отсюда.

export interface City {
    code: number;
    city: string;
    full_name: string;
    country_code: string | null;
}

const STORAGE_KEY = "delivery_city";
// Ключ прежнего формата: город лежал внутри общего выбора доставки
const LEGACY_KEY = "delivery_choice";

export const useCity = () => {
    const city = useState<City | null>("city", () => null);
    // Автоопределение пробуем один раз за сессию, иначе каждый переход
    // по сайту дёргал бы геосервис
    const detected = useState<boolean>("city-detected", () => false);
    const detecting = useState<boolean>("city-detecting", () => false);

    const persist = () => {
        if (import.meta.server) return;
        try {
            if (city.value) localStorage.setItem(STORAGE_KEY, JSON.stringify(city.value));
            else localStorage.removeItem(STORAGE_KEY);
        } catch {
            // приватный режим браузера — не критично
        }
    };

    const restore = (): boolean => {
        if (import.meta.server || city.value) return !!city.value;

        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                city.value = JSON.parse(raw);
                return true;
            }

            // Переносим выбор, сделанный до появления общего города
            const legacy = localStorage.getItem(LEGACY_KEY);
            if (legacy) {
                const saved = JSON.parse(legacy);
                if (saved?.city?.code) {
                    city.value = saved.city;
                    persist();
                    return true;
                }
            }
        } catch {
            // повреждённое значение просто игнорируем
        }

        return false;
    };

    const setCity = (value: City | null) => {
        city.value = value;
        // Ручной выбор отменяет автоопределение: перебивать его нельзя
        detected.value = true;
        persist();
    };

    const search = async (query: string): Promise<City[]> => {
        const term = query.trim();
        if (term.length < 2) return [];
        try {
            return await $fetch<City[]>("/api/delivery/cities", { query: { q: term } });
        } catch {
            return [];
        }
    };

    // Спрашиваем сервер только если покупатель ещё ничего не выбирал.
    // Определение может не сработать (нет токена геосервиса, город не нашёлся
    // в справочнике СДЭК) — тогда просто остаёмся без города
    const detect = async () => {
        if (import.meta.server || detected.value || city.value || detecting.value) return;

        detecting.value = true;
        try {
            const found = await $fetch<City | null>("/api/delivery/detect_city");
            if (found?.code) {
                city.value = found;
                persist();
            }
        } catch {
            // молча: без города сайт работает, просто нужен ручной выбор
        } finally {
            detected.value = true;
            detecting.value = false;
        }
    };

    const ensure = async () => {
        if (restore()) return;
        await detect();
    };

    return { city, detecting, setCity, search, restore, detect, ensure };
};
